/**
 * Helpers Utils
 * this file contains general helpers that is not related to a specific category
 *
 * Naming Convention: camelCase
 * i.e. sayHello
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

// utils
import _each from 'lodash/each';
import _includes from 'lodash/includes';
import _get from 'lodash/get';
import _forEach from 'lodash/forEach';
import _isArray from 'lodash/isArray';
import _isFunction from 'lodash/isFunction';
import _isPlainObject from 'lodash/isPlainObject';
import _set from 'lodash/set';
import { $http, replaceParams } from '@tajawal/web-core-sdk-v2';
import { isSSR } from './compute.utils';

// constants
import { DESKTOP_FLIGHTS_ROUTES, FLIGHTS_ROUTES, TEST_ID_DELIMITER } from '@/constants/common.constants';

// types
import { Translations } from '@/types/middleware.types';
import { ParsedUrlQuery } from 'querystring';
import { Language } from '@tajawal/web-core-sdk-v2/lib/types/common.types';

export function initI18n(language: string, translations: Translations): void {
  i18next.use(initReactI18next).init({
    initImmediate: false,
    lng: language,
    debug: false,
    resources: {
      [language]: {
        translation: translations?.[language],
      },
    },
    interpolation: {
      prefix: '{',
      suffix: '}',
    },
    react: {
      useSuspense: false,
    },
  });
}

export function buildTestId(...args: Array<string | number>): string {
  return args.join(TEST_ID_DELIMITER);
}

export function scrollToElement({
  id,
  yOffset,
  behavior = 'smooth',
}: {
  id: string;
  yOffset: number;
  behavior?: ScrollBehavior;
}) {
  const elem = document.getElementById(id);
  if (elem) {
    const y = elem.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior });
  }
}

export function setFieldTimeout(callback: (...args: any[]) => any, timeout: number, groupId = 'primary') {
  const win: any = window; // to suppress TS errors
  const timeoutId = setTimeout(() => {
    const timeoutIds = _get(win.timeoutIdMap, groupId, []);
    _each(timeoutIds, (tId) => clearTimeout(tId));
    _set(win.timeoutIdMap, groupId, []);
    _isFunction(callback) && callback();
  }, timeout);

  if (!_isPlainObject(win.timeoutIdMap)) {
    win.timeoutIdMap = {};
    _set(win.timeoutIdMap, groupId, []);
  }

  _isArray(_get(win.timeoutIdMap, groupId)) && win.timeoutIdMap[groupId].push(timeoutId);
}

export function constructMwebPath(path: string) {
  return `/mweb/${path}`;
}

export function isGoogleFlight(path: ParsedUrlQuery) {
  return Boolean(path.referer?.includes('google-flights') || path.referer?.includes('google-api'));
}

export function getPaymentRedirectUrl(saleId: string, isMobile: boolean, lang: Language): string {
  const routeConfig = isMobile ? FLIGHTS_ROUTES : DESKTOP_FLIGHTS_ROUTES;

  const baseUrl = replaceParams(routeConfig.PAYMENT, { saleId, lang });
  return `${baseUrl}?updated_at=${new Date().getTime()}&productType=flight`;
}

export function getRedirectUrlFromPax(
  saleId: string,
  hasExtrasPage: boolean,
  isNCR: boolean,
  isMobile = true,
  lang: Language = 'en'
): string {
  const routeConfig = isMobile ? FLIGHTS_ROUTES : DESKTOP_FLIGHTS_ROUTES;
  const url = hasExtrasPage
    ? replaceParams(routeConfig.EXTRAS, { saleId, lang })
    : getPaymentRedirectUrl(saleId, isMobile, lang);
  const [baseUrl, queries = ''] = url.split('?');
  return `${baseUrl}?${queries}${isNCR ? '&ncr=1' : ''}`;
}

export function copyToClipboard(copiedText: string): void {
  let input;

  function createInput(text) {
    input = document.createElement('input');
    input.readOnly = true;
    input.contentEditable = true;
    input.value = text;
    document.body.appendChild(input);
  }

  function selectText() {
    input.select();
    input.setSelectionRange(0, 99999);
  }

  async function copyCode() {
    if (window.isSecureContext && navigator.clipboard) {
      await navigator.clipboard.writeText(copiedText);
      // @ts-ignore
    } else if (window.clipboardData) {
      // @ts-ignore
      window.clipboardData.setData('Text', copiedText);
    }
    document.execCommand('copy');
    document.body.removeChild(input);
  }
  createInput(copiedText);
  selectText();
  copyCode();
}

export function isMetaUrl(metaPlatform) {
  if (isSSR()) return false;
  return (
    _includes(window.location.search, `utm_source=${metaPlatform}`) &&
    _includes(window.location.search, 'utm_medium=meta')
  );
}

export function cancelAllPendingRequests() {
  _forEach($http.pendingRequests, (canceller) => canceller());
}

export function isMeta() {
  return !isSSR() && window.location.search.includes('meta');
}

export function getXAppName(isMobile: boolean) {
  return isMobile ? 'ct-web-flights-app-mobile-almosafer' : 'ct-web-flights-app-desktop-almosafer';
}
